import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useCropMethodology,
  useGetFootprintingCropPrimaryDataById,
  useMutateFootprintingCropPrimaryData,
  useTranslation,
} from "utils";

import {
  CFP_CROP_IRRIGATION_APPLICATION_METHOD,
  CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE,
  CFP_CROP_IRRIGATION_WATER_SOURCE,
  ENUM_CFP_CROP_IRRIGATION_APPLICATION_METHOD,
  ENUM_CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE,
  ENUM_CFP_CROP_IRRIGATION_WATER_SOURCE,
} from "models";

import { Card, Flex, Form, FormInstance, Icon, InputNumber, KlimRadioGroup, Option, PageSpin, Select, Tooltip, Typography } from "components";

type CropIrrigationFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

const CropIrrigationForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropIrrigationFormProps) => {
  const { t } = useTranslation("footprintingCrop");
  const formRef = useRef<FormInstance>(null);
  const { isCfp } = useCropMethodology(farmId);

  const [irrigationActive, setIrrigationActive] = useState<boolean | null>(null);
  const [waterUsedM3, setWaterUsedM3] = useState<number | null>(null);
  const [irrigationApplicationMethod, setIrrigationApplicationMethod] = useState<ENUM_CFP_CROP_IRRIGATION_APPLICATION_METHOD | null>(null);
  const [irrigationWaterSource, setIrrigationWaterSource] = useState<ENUM_CFP_CROP_IRRIGATION_WATER_SOURCE | null>(null);
  const [irrigationPumpPowerSource, setIrrigationPumpPowerSource] = useState<ENUM_CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE | null>(null);
  const [irrigationAreaPct, setIrrigationAreaPct] = useState<number | null>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      irrigationActive,
      waterUsedM3,
      irrigationApplicationMethod,
      irrigationWaterSource,
      irrigationPumpPowerSource,
      irrigationAreaPct,
    },
    primaryDataId
  );

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setIrrigationActive(cropPrimaryDataQuery.data.irrigationActive ?? null);
      setWaterUsedM3(cropPrimaryDataQuery.data.waterUsedM3 ?? null);
      setIrrigationApplicationMethod(cropPrimaryDataQuery.data.irrigationApplicationMethod ?? null);
      setIrrigationWaterSource(cropPrimaryDataQuery.data.irrigationWaterSource ?? null);
      setIrrigationPumpPowerSource(cropPrimaryDataQuery.data.irrigationPumpPowerSource ?? null);
      setIrrigationAreaPct(cropPrimaryDataQuery.data.irrigationAreaPct ?? null);

      formRef.current?.setFieldsValue({
        irrigationActive: cropPrimaryDataQuery.data.irrigationActive ?? null,
        waterUsedM3: cropPrimaryDataQuery.data.waterUsedM3 ?? null,
        irrigationApplicationMethod: cropPrimaryDataQuery.data.irrigationApplicationMethod ?? null,
        irrigationWaterSource: cropPrimaryDataQuery.data.irrigationWaterSource ?? null,
        irrigationPumpPowerSource: cropPrimaryDataQuery.data.irrigationPumpPowerSource ?? null,
        irrigationAreaPct: cropPrimaryDataQuery.data.irrigationAreaPct ?? null,
      });

      formRef.current?.validateFields();
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  const isLoading = queryIsLoading(cropPrimaryDataQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-crop-irrigation-form" className="padding-100">
      <Flex align="center" gap={16} justify="space-between" className="padding-100">
        <Typography variant="paragraph-m">
          {t("cropIrrigation.question.irrigation")}
          {true && <span className="required-asterisk">*</span>}
          <Tooltip title={t("cropIrrigation.tooltips.irrigation")}>
            <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
          </Tooltip>
        </Typography>
        <KlimRadioGroup
          required
          name="irrigationActive"
          dataTestId="footprinting-crop-irrigation-radio"
          disabled={readOnly}
          vertical={false}
          type="tertiary"
          size="large"
          onChange={({ target }) => {
            setIrrigationActive(target.value);
          }}
          value={irrigationActive}
          defaultValue={irrigationActive}
          optionType="button"
          options={[true, false].map((opt) => ({
            label: t(`footprinting:radioAnswer.${opt}`),
            value: opt,
          }))}
        />
      </Flex>
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          {irrigationActive && (
            <InputNumber
              className="floating-label"
              disabled={readOnly}
              name="waterUsedM3"
              dataTestId="footprinting-crop-water-input"
              label={t("cropIrrigation.question.waterUsedQuestionLabel")}
              tooltip={t("cropIrrigation.tooltips.waterUsedQuestionLabel")}
              suffix="m³"
              value={waterUsedM3}
              initialValue={waterUsedM3}
              onChange={(v) => setWaterUsedM3(v)}
              rules={[
                { required: true, message: t("cropIrrigation.error.waterUsedLabelError") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                  message: t("cropIrrigation.error.waterUsedLabelError"),
                },
              ]}
            />
          )}
          {isCfp && irrigationActive && (
            <>
              <Select
                disabled={readOnly}
                name="irrigationApplicationMethod"
                dataTestId="footprinting-crop-irrigation-application-method-input"
                label={t("cropIrrigation.question.irrigationApplicationMethod")}
                value={irrigationApplicationMethod}
                initialValue={irrigationApplicationMethod}
                onChange={(v: ENUM_CFP_CROP_IRRIGATION_APPLICATION_METHOD) => setIrrigationApplicationMethod(v)}
              >
                {Object.values(CFP_CROP_IRRIGATION_APPLICATION_METHOD).map((method) => (
                  <Option key={method} value={method}>
                    {t(`cropIrrigation.applicationMethod.${method}`)}
                  </Option>
                ))}
              </Select>
              <Select
                disabled={readOnly}
                name="irrigationWaterSource"
                dataTestId="footprinting-crop-irrigation-water-source-input"
                label={t("cropIrrigation.question.irrigationWaterSource")}
                value={irrigationWaterSource}
                initialValue={irrigationWaterSource}
                onChange={(v: ENUM_CFP_CROP_IRRIGATION_WATER_SOURCE) => setIrrigationWaterSource(v)}
              >
                {Object.values(CFP_CROP_IRRIGATION_WATER_SOURCE).map((source) => (
                  <Option key={source} value={source}>
                    {t(`cropIrrigation.waterSource.${source}`)}
                  </Option>
                ))}
              </Select>
              <Select
                disabled={readOnly}
                name="irrigationPumpPowerSource"
                dataTestId="footprinting-crop-irrigation-pump-power-source-input"
                label={t("cropIrrigation.question.irrigationPumpPowerSource")}
                value={irrigationPumpPowerSource}
                initialValue={irrigationPumpPowerSource}
                onChange={(v: ENUM_CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE) => setIrrigationPumpPowerSource(v)}
              >
                {Object.values(CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE).map((source) => (
                  <Option key={source} value={source}>
                    {t(`cropIrrigation.pumpPowerSource.${source}`)}
                  </Option>
                ))}
              </Select>
              <InputNumber
                className="floating-label"
                disabled={readOnly}
                name="irrigationAreaPct"
                dataTestId="footprinting-crop-irrigation-area-pct-input"
                label={t("cropIrrigation.question.irrigationAreaPct")}
                suffix="%"
                value={irrigationAreaPct}
                initialValue={irrigationAreaPct}
                onChange={(v) => setIrrigationAreaPct(v)}
                rules={[
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) =>
                      isNullOrUndefined(v) || (isValidNonnegativeNumber(v) && v <= 100) ? Promise.resolve() : Promise.reject(),
                    message: t("cropIrrigation.error.irrigationAreaPctError"),
                  },
                ]}
              />
            </>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

CropIrrigationForm.Key = "crop.irrigation";
export { CropIrrigationForm };
